import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// ── Interfaces ────────────────────────────────────────
interface Product {
  id: number;
  name: string;
  title?: string;
  price: string;
  image: string | null;
  short_description?: string;
  category_name?: string;
  is_featured: number;
  cultivation_stage?: string;
}

interface Article {
  id: number;
  title: string;
  summary: string;
  content: string;
  image: string | null;
  category: string;
  cultivation_stage: string;
  author_name?: string;
  views: number;
  created_at: string;
  is_featured: number;
  status?: string;
}

interface Experience {
  id: number;
  title: string;
  product_type: string;
  challenge: string;
  solution: string | null;
  user_name: string;
  created_at: string;
}

interface Advertisement {
  id: number;
  title: string;
  type: string;
  price: string | null;
  category: string;
  description: string;
  contact_phone: string;
  province: string | null;
  user_name?: string;
  created_at: string;
  status?: string;
}

// ── کامپوننت اسلایدر افقی ──────────────────────────────
interface SliderProps {
  title: string;
  icon: string;
  viewAllLink: string;
  viewAllText: string;
  children: React.ReactNode;
  itemsCount: number;
}

const HorizontalSlider: React.FC<SliderProps> = ({ title, icon, viewAllLink, viewAllText, children, itemsCount }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  if (itemsCount === 0) return null;

  return (
    <section style={{ marginBottom: '50px', direction: 'rtl' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '18px',
        padding: '0 20px',
        maxWidth: '1400px',
        margin: '0 auto 18px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '26px' }}>{icon}</span>
          <h2 style={{ fontSize: '22px', color: '#1e3a1e', margin: 0, fontWeight: '700' }}>
            {title}
          </h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Link
            to={viewAllLink}
            style={{
              color: '#2e7d32',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '600',
              padding: '6px 14px',
              background: '#e8f5e9',
              borderRadius: '20px',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#c8e6c9'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#e8f5e9'}
          >
            {viewAllText} ←
          </Link>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => scroll('right')}
              aria-label="قبلی"
              style={{
                width: '38px', height: '38px', borderRadius: '50%',
                border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '18px', color: '#2e7d32', transition: 'all 0.2s',
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#2e7d32'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#2e7d32'; }}
            >
              ›
            </button>
            <button
              onClick={() => scroll('left')}
              aria-label="بعدی"
              style={{
                width: '38px', height: '38px', borderRadius: '50%',
                border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '18px', color: '#2e7d32', transition: 'all 0.2s',
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#2e7d32'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#2e7d32'; }}
            >
              ‹
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        style={{
          display: 'flex', gap: '20px', overflowX: 'auto', overflowY: 'hidden',
          padding: '10px 20px 20px', scrollBehavior: 'smooth',
          scrollbarWidth: 'thin', maxWidth: '1400px', margin: '0 auto',
          scrollSnapType: 'x mandatory'
        }}
        className="horizontal-scroll"
      >
        {children}
      </div>

      <style>{`
        .horizontal-scroll::-webkit-scrollbar { height: 6px; }
        .horizontal-scroll::-webkit-scrollbar-track { background: #f0f4f0; border-radius: 10px; }
        .horizontal-scroll::-webkit-scrollbar-thumb { background: #2e7d32; border-radius: 10px; }
        .horizontal-scroll::-webkit-scrollbar-thumb:hover { background: #1b5e20; }
        .horizontal-scroll > * { scroll-snap-align: start; }
      `}</style>
    </section>
  );
};

// ── کامپوننت کارت دسته‌بندی ─────────────────────────────
interface CategoryCardProps {
  title: string;
  description: string;
  icon: string;
  link: string;
  gradient: string;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ title, description, icon, link, gradient }) => (
  <Link to={link} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
    <div
      style={{
        background: gradient,
        borderRadius: '20px',
        padding: '30px 25px',
        color: '#fff',
        minHeight: '180px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'transform 0.25s, box-shadow 0.25s',
        cursor: 'pointer',
        boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-6px)';
        e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.18)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
      }}
    >
      <div style={{
        position: 'absolute', top: '-20px', left: '-20px',
        fontSize: '120px', opacity: 0.15, pointerEvents: 'none'
      }}>
        {icon}
      </div>
      <div style={{ fontSize: '42px', marginBottom: '15px' }}>{icon}</div>
      <div>
        <h3 style={{ margin: 0, fontSize: '19px', fontWeight: '700' }}>{title}</h3>
        <p style={{ margin: '8px 0 0', fontSize: '13px', opacity: 0.9, lineHeight: '1.6' }}>
          {description}
        </p>
      </div>
      <div style={{
        marginTop: '15px', display: 'inline-flex', alignItems: 'center', gap: '5px',
        fontSize: '13px', fontWeight: '600',
        background: 'rgba(255,255,255,0.2)',
        padding: '5px 14px', borderRadius: '20px', alignSelf: 'flex-start'
      }}>
        مشاهده →
      </div>
    </div>
  </Link>
);

// ── کامپوننت اصلی Home ──────────────────────────────────
const Home: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<Article[]>([]);
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);

    // ── ۱. محصولات ویژه ──────────────────────────────
    try {
      const res = await fetch('https://fellfell.ir/api/products.php', { credentials: 'include' });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        const featured = data.data.filter((p: Product) => p.is_featured === 1);
        setFeaturedProducts(featured.length > 0 ? featured : data.data.slice(0, 10));
      }
    } catch (err) {
      console.error('خطا در محصولات:', err);
    }

    // ── ۲. مقالات ویژه ──────────────────────────────
    try {
      const res = await fetch('https://fellfell.ir/api/articles.php', { credentials: 'include' });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        const published = data.data.filter((a: Article) => a.status === 'published' || !a.status);
        const featured = published.filter((a: Article) => a.is_featured === 1);
        setFeaturedArticles(featured.length > 0 ? featured : published.slice(0, 10));
      }
    } catch (err) {
      console.error('خطا در مقالات:', err);
    }

    // ── ۳. تجارب کاری ───────────────────────────────
    try {
      const res = await fetch('https://fellfell.ir/api/experiences.php', { credentials: 'include' });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setExperiences(data.data.slice(0, 10));
      }
    } catch (err) {
      console.error('خطا در تجارب:', err);
    }

    // ── ۴. آگهی‌ها ──────────────────────────────────
    try {
      const res = await fetch('https://fellfell.ir/api/advertisements.php', { credentials: 'include' });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        const approved = data.data.filter((a: Advertisement) => a.status === 'approved' || !a.status);
        setAdvertisements(approved.slice(0, 10));
      }
    } catch (err) {
      console.error('خطا در آگهی‌ها:', err);
    }

    setLoading(false);
  };

  const stripHtml = (html: string): string => {
    if (!html) return '';
    let text = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    text = text.replace(/<[^>]+>/g, ' ');
    text = text.replace(/\s+/g, ' ').trim();
    return text;
  };

  const getStageBadge = (stage: string) => {
    const stages: Record<string, { label: string; color: string; bg: string; icon: string }> = {
      'کشت': { label: 'کشت', color: '#2e7d32', bg: '#e8f5e9', icon: '🌱' },
      'داشت': { label: 'داشت', color: '#1565c0', bg: '#e3f2fd', icon: '🌿' },
      'برداشت': { label: 'برداشت', color: '#f57c00', bg: '#fff3e0', icon: '🌾' },
      'نگهداشت': { label: 'نگهداشت', color: '#6a1b9a', bg: '#f3e5f5', icon: '🏺' }
    };
    return stages[stage] || null;
  };

  const getAdTypeLabel = (type: string) => {
    const types: Record<string, { label: string; color: string }> = {
      'sell': { label: 'فروش', color: '#2e7d32' },
      'buy': { label: 'خرید', color: '#1565c0' },
      'request': { label: 'درخواست', color: '#f57c00' },
      'cooperation': { label: 'همکاری', color: '#6a1b9a' }
    };
    return types[type] || { label: type, color: '#333' };
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px', height: '50px',
            border: '4px solid #f0f0f0', borderTopColor: '#2e7d32',
            borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto'
          }} />
          <p style={{ marginTop: '15px', color: '#888' }}>⏳ در حال بارگذاری...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ direction: 'rtl', background: '#f8fbf8', minHeight: '100vh', paddingBottom: '60px' }}>

      {/* ═══ Hero Section ═══ */}
      <section style={{
        background: 'linear-gradient(135deg, #D6ECD7, #a8d5ba)',
        padding: '70px 20px',
        marginBottom: '50px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '42px', color: '#1e3a1e', margin: 0 }}>
            به <strong style={{ color: '#2E7D32' }}>سبزه میدون</strong> خوش آمدید
          </h1>
          <p style={{ fontSize: '18px', color: '#3a5a3a', marginTop: '10px' }}>
            تأمین‌کننده تخصصی نهاده‌های کشاورزی
          </p>
          <Link
            to="/shop"
            style={{
              display: 'inline-block',
              marginTop: '20px',
              padding: '12px 35px',
              background: '#2e7d32',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: '600',
              boxShadow: '0 4px 15px rgba(46,125,50,0.3)',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            🛒 مشاهده محصولات
          </Link>
        </div>
      </section>

      {/* ═══ ۱. محصولات ویژه ═══ */}
      <HorizontalSlider
        title="محصولات ویژه"
        icon="🛒"
        viewAllLink="/shop"
        viewAllText="مشاهده همه"
        itemsCount={featuredProducts.length}
      >
        {featuredProducts.map((product) => {
          const productName = product.title || product.name || 'بدون نام';
          const stage = product.cultivation_stage ? getStageBadge(product.cultivation_stage) : null;
          return (
            <Link key={product.id} to={`/product/${product.id}`}
              style={{ textDecoration: 'none', color: 'inherit', minWidth: '240px', maxWidth: '240px', flexShrink: 0 }}>
              <div style={{
                background: '#fff', borderRadius: '16px', padding: '15px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.06)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                height: '100%', display: 'flex', flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.06)';
              }}
              >
                <div style={{ position: 'relative' }}>
                  <img
                    src={product.image ? `https://fellfell.ir/uploads/products/${product.image}` : 'https://fellfell.ir/assets/images/product-placeholder.jpg'}
                    alt={productName}
                    style={{ width: '100%', height: '180px', objectFit: 'cover', borderRadius: '12px', background: '#f0f4f0' }}
                    onError={(e) => { e.currentTarget.src = 'https://fellfell.ir/assets/images/product-placeholder.jpg'; }}
                    loading="lazy"
                    decoding="async"
                  />
                  <span style={{
                    position: 'absolute', top: '8px', right: '8px',
                    background: '#fff3e0', color: '#e65100',
                    padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600'
                  }}>⭐ ویژه</span>
                  {stage && (
                    <span style={{
                      position: 'absolute', top: '8px', left: '8px',
                      background: stage.bg, color: stage.color,
                      padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600'
                    }}>{stage.icon} {stage.label}</span>
                  )}
                </div>
                <h3 style={{
                  fontSize: '15px', color: '#1e3a1e', margin: '12px 0 8px',
                  fontWeight: '600', lineHeight: '1.5', height: '45px', overflow: 'hidden'
                }}>{productName}</h3>
                <p style={{
                  fontSize: '13px', color: '#888', margin: '0 0 10px',
                  height: '36px', overflow: 'hidden', lineHeight: '1.6'
                }}>{product.short_description || ''}</p>
                <p style={{ color: '#2e7d32', fontSize: '16px', fontWeight: '700', margin: 'auto 0 0' }}>
                  {new Intl.NumberFormat('fa-IR').format(Number(product.price))} تومان
                </p>
              </div>
            </Link>
          );
        })}
      </HorizontalSlider>

      {/* ═══ ۲. مقالات ویژه ═══ */}
      <HorizontalSlider
        title="مقالات ویژه"
        icon="📰"
        viewAllLink="/articles"
        viewAllText="مشاهده همه"
        itemsCount={featuredArticles.length}
      >
        {featuredArticles.map((article) => {
          const stage = getStageBadge(article.cultivation_stage || 'کشت');
          return (
            <Link key={article.id} to={`/article/${article.id}`}
              style={{ textDecoration: 'none', color: 'inherit', minWidth: '280px', maxWidth: '280px', flexShrink: 0 }}>
              <div style={{
                background: '#fff', borderRadius: '16px', padding: '15px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.06)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                height: '100%', display: 'flex', flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.06)';
              }}
              >
                <div style={{ position: 'relative' }}>
                  <img
                    src={article.image ? `https://fellfell.ir/uploads/articles/${article.image}` : 'https://fellfell.ir/assets/images/article-placeholder.jpg'}
                    alt={article.title}
                    style={{ width: '100%', height: '160px', objectFit: 'cover', borderRadius: '12px', background: '#f0f4f0' }}
                    onError={(e) => { e.currentTarget.src = 'https://fellfell.ir/assets/images/article-placeholder.jpg'; }}
                    loading="lazy"
                    decoding="async"
                  />
                  {stage && (
                    <span style={{
                      position: 'absolute', top: '8px', right: '8px',
                      background: stage.bg, color: stage.color,
                      padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600'
                    }}>{stage.icon} {stage.label}</span>
                  )}
                </div>
                <h3 style={{
                  fontSize: '15px', color: '#1e3a1e', margin: '12px 0 8px',
                  fontWeight: '600', lineHeight: '1.5', height: '45px', overflow: 'hidden'
                }}>{article.title}</h3>
                <p style={{
                  color: '#666', fontSize: '13px', lineHeight: '1.7',
                  flex: 1, margin: '0 0 12px', height: '60px', overflow: 'hidden'
                }}>
                  {stripHtml(article.summary || article.content || '').substring(0, 80)}...
                </p>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: '12px', color: '#888',
                  paddingTop: '8px', borderTop: '1px solid #f0f0f0'
                }}>
                  <span>👤 {article.author_name || 'نویسنده'}</span>
                  <span>👁️ {article.views || 0}</span>
                </div>
              </div>
            </Link>
          );
        })}
      </HorizontalSlider>

      {/* ═══ ۳. تجارب کشاورزان ═══ */}
      <HorizontalSlider
        title="تجارب کشاورزان"
        icon="🌾"
        viewAllLink="/experiences"
        viewAllText="مشاهده همه"
        itemsCount={experiences.length}
      >
        {experiences.map((exp) => (
          <Link key={exp.id} to={`/experience/${exp.id}`}
            style={{ textDecoration: 'none', color: 'inherit', minWidth: '280px', maxWidth: '280px', flexShrink: 0 }}>
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f5faf5 100%)',
              borderRadius: '16px', padding: '18px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.06)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              height: '100%', display: 'flex', flexDirection: 'column',
              borderRight: '4px solid #2e7d32'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(46,125,50,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.06)';
            }}
            >
              <span style={{
                display: 'inline-block', background: '#e8f5e9', color: '#2e7d32',
                padding: '3px 12px', borderRadius: '20px', fontSize: '12px',
                marginBottom: '10px', alignSelf: 'flex-start'
              }}>{exp.product_type || 'تجربه کشاورزی'}</span>
              <h3 style={{
                fontSize: '15px', color: '#1e3a1e', margin: '0 0 10px',
                fontWeight: '600', lineHeight: '1.5', height: '45px', overflow: 'hidden'
              }}>{exp.title}</h3>
              <p style={{
                color: '#666', fontSize: '13px', lineHeight: '1.7',
                flex: 1, margin: '0 0 12px', height: '60px', overflow: 'hidden'
              }}>{exp.challenge?.substring(0, 80)}...</p>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: '12px', color: '#888',
                paddingTop: '10px', borderTop: '1px solid #e8f0e8'
              }}>
                <span>👤 {exp.user_name || 'کشاورز'}</span>
                <span>📅 {new Date(exp.created_at).toLocaleDateString('fa-IR')}</span>
              </div>
            </div>
          </Link>
        ))}
      </HorizontalSlider>

      {/* ═══ ۴. آگهی‌ها ═══ */}
      <HorizontalSlider
        title="آگهی‌های کشاورزی"
        icon="📢"
        viewAllLink="/advertisements"
        viewAllText="مشاهده همه"
        itemsCount={advertisements.length}
      >
        {advertisements.map((ad) => {
          const typeInfo = getAdTypeLabel(ad.type);
          return (
            <Link key={ad.id} to={`/advertisement/${ad.id}`}
              style={{ textDecoration: 'none', color: 'inherit', minWidth: '270px', maxWidth: '270px', flexShrink: 0 }}>
              <div style={{
                background: '#fff', borderRadius: '16px', padding: '18px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.06)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                height: '100%', display: 'flex', flexDirection: 'column',
                borderTop: `4px solid ${typeInfo.color}`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.06)';
              }}
              >
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <span style={{
                    background: typeInfo.color + '20', color: typeInfo.color,
                    padding: '3px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600'
                  }}>{typeInfo.label}</span>
                  {ad.category && (
                    <span style={{
                      background: '#f5f5f5', color: '#666',
                      padding: '3px 12px', borderRadius: '20px', fontSize: '12px'
                    }}>{ad.category}</span>
                  )}
                </div>
                <h3 style={{
                  fontSize: '15px', color: '#1e3a1e', margin: '0 0 10px',
                  fontWeight: '600', lineHeight: '1.5', height: '45px', overflow: 'hidden'
                }}>{ad.title}</h3>
                <p style={{
                  color: '#666', fontSize: '13px', lineHeight: '1.7',
                  flex: 1, margin: '0 0 12px', height: '40px', overflow: 'hidden'
                }}>{ad.description?.substring(0, 60)}...</p>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', paddingTop: '10px', borderTop: '1px solid #f0f0f0'
                }}>
                  <span style={{ color: '#2e7d32', fontSize: '14px', fontWeight: '700' }}>
                    {ad.price ? new Intl.NumberFormat('fa-IR').format(Number(ad.price)) + ' تومان' : 'توافقی'}
                  </span>
                  <span style={{ fontSize: '12px', color: '#888' }}>📞 {ad.contact_phone}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#aaa', marginTop: '8px' }}>
                  <span>👤 {ad.user_name || 'کاربر'}</span>
                  <span>📍 {ad.province || 'نامشخص'}</span>
                </div>
              </div>
            </Link>
          );
        })}
      </HorizontalSlider>

      {/* ═══ ۵. بذر و کود ═══ */}
      <section style={{ maxWidth: '1400px', margin: '0 auto 50px', padding: '0 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '25px' }}>
          <span style={{ fontSize: '28px' }}>🌱</span>
          <h2 style={{ fontSize: '24px', color: '#1e3a1e', margin: 0, fontWeight: '700' }}>
            بذر و کود
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '22px' }}>
          <CategoryCard title="بذر و نشا" description="بذرهای اصلاح‌شده و نشاهای باکیفیت برای کشت گلخانه‌ای و مزرعه‌ای" icon="🌾" link="/shop?category=seeds" gradient="linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)" />
          <CategoryCard title="کودهای آلی" description="کودهای ارگانیک، ورمی‌کمپوست و کمپوست برای تقویت خاک" icon="🧪" link="/shop?category=organic-fertilizers" gradient="linear-gradient(135deg, #33691e 0%, #689f38 100%)" />
          <CategoryCard title="کودهای شیمیایی" description="کودهای نیتروژنی، فسفاته و پتاسیمی برای رشد بهینه گیاهان" icon="⚗️" link="/shop?category=chemical-fertilizers" gradient="linear-gradient(135deg, #004d40 0%, #00796b 100%)" />
          <CategoryCard title="کودهای میکرو" description="عناصر ریزمغذی مانند آهن، روی، منگنز و بور برای رفع کمبودها" icon="🔬" link="/shop?category=micro-nutrients" gradient="linear-gradient(135deg, #1a237e 0%, #283593 100%)" />
        </div>
      </section>

      {/* ═══ ۶. سموم کشاورزی ═══ */}
      <section style={{ maxWidth: '1400px', margin: '0 auto 50px', padding: '0 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '25px' }}>
          <span style={{ fontSize: '28px' }}>💊</span>
          <h2 style={{ fontSize: '24px', color: '#1e3a1e', margin: 0, fontWeight: '700' }}>
            سموم کشاورزی
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '22px' }}>
          <CategoryCard title="حشره‌کش‌ها" description="سموم تخصصی برای کنترل آفات مکنده، جونده و برگ‌خوار" icon="🐛" link="/shop?category=insecticides" gradient="linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%)" />
          <CategoryCard title="قارچ‌کش‌ها" description="کنترل بیماری‌های قارچی مانند سفیدک، کپک و پوسیدگی" icon="🍄" link="/shop?category=fungicides" gradient="linear-gradient(135deg, #4a148c 0%, #6a1b9a 100%)" />
          <CategoryCard title="علف‌کش‌ها" description="سموم انتخابی و عمومی برای کنترل علف‌های هرز" icon="🌿" link="/shop?category=herbicides" gradient="linear-gradient(135deg, #e65100 0%, #ef6c00 100%)" />
          <CategoryCard title="کنه‌کش‌ها" description="سموم موثر برای کنترل کنه‌های گیاهی و حیوانی" icon="🕷️" link="/shop?category=acaricides" gradient="linear-gradient(135deg, #3e2723 0%, #5d4037 100%)" />
        </div>
      </section>

      {/* ═══ ۷. چهار مرحله کشت ═══ */}
      <section style={{ maxWidth: '1400px', margin: '0 auto 50px', padding: '0 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '25px' }}>
          <span style={{ fontSize: '28px' }}>🌿</span>
          <h2 style={{ fontSize: '24px', color: '#1e3a1e', margin: 0, fontWeight: '700' }}>
            مراحل کشت و داشت
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '22px' }}>
          <CategoryCard title="🌱 کشت (کاشت)" description="آماده‌سازی زمین، انتخاب بذر، زمان کاشت و روش‌های کشت" icon="🌱" link="/articles?stage=کشت" gradient="linear-gradient(135deg, #2e7d32 0%, #43a047 100%)" />
          <CategoryCard title="🌿 داشت (رشد)" description="آبیاری، تغذیه، کنترل آفات و بیماری‌ها در طول رشد" icon="🌿" link="/articles?stage=داشت" gradient="linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)" />
          <CategoryCard title="🌾 برداشت" description="زمان مناسب برداشت، روش‌های برداشت و بسته‌بندی محصول" icon="🌾" link="/articles?stage=برداشت" gradient="linear-gradient(135deg, #f57c00 0%, #fb8c00 100%)" />
          <CategoryCard title="🏺 نگهداشت (انبارداری)" description="اصول انبارداری، نگهداری محصول و جلوگیری از ضایعات" icon="🏺" link="/articles?stage=نگهداشت" gradient="linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%)" />
        </div>
      </section>

      {/* ═══ ۸. مکانیزاسیون کشاورزی ═══ */}
      <section style={{ maxWidth: '1400px', margin: '0 auto 50px', padding: '0 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '25px' }}>
          <span style={{ fontSize: '28px' }}>🚜</span>
          <h2 style={{ fontSize: '24px', color: '#1e3a1e', margin: 0, fontWeight: '700' }}>
            مکانیزاسیون کشاورزی
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '22px' }}>
          <CategoryCard title="ادوات کشاورزی" description="تراکتور، تیلر، ادوات خاک‌ورزی، کاشت و برداشت" icon="🚜" link="/shop?category=agricultural-tools" gradient="linear-gradient(135deg, #bf360c 0%, #e64a19 100%)" />
          <CategoryCard title="گلخانه صنعتی" description="سازه‌ها، تجهیزات گرمایشی، سرمایشی و کنترل اقلیم گلخانه" icon="🏭" link="/shop?category=greenhouse" gradient="linear-gradient(135deg, #006064 0%, #0097a7 100%)" />
          <CategoryCard title="آبیاری مدرن" description="سیستم‌های قطره‌ای، بارانی، زیرسطحی و اتوماسیون آبیاری" icon="💧" link="/shop?category=modern-irrigation" gradient="linear-gradient(135deg, #01579b 0%, #0277bd 100%)" />
          <CategoryCard title="ماشین‌آلات برداشت" description="کمباین، دروگر، بسته‌بند و تجهیزات برداشت مکانیزه" icon="⚙️" link="/shop?category=harvesting-machinery" gradient="linear-gradient(135deg, #4e342e 0%, #6d4c41 100%)" />
          <CategoryCard title="تجهیزات دامداری" description="شیردوش، میکسر، دانخوری و تجهیزات مدرن دامداری" icon="🐄" link="/shop?category=livestock-equipment" gradient="linear-gradient(135deg, #1b5e20 0%, #388e3c 100%)" />
          <CategoryCard title="سیستم‌های هوشمند" description="سنسورها، مانیتورینگ، اتوماسیون و کشاورزی دقیق" icon="📡" link="/shop?category=smart-systems" gradient="linear-gradient(135deg, #283593 0%, #3949ab 100%)" />
        </div>
      </section>

    </div>
  );
};

export default Home;